import { useState, useEffect, useCallback, useRef } from 'react'
import {
  getReport,
  updateReport,
  type ItemReport,
  type ExtractedItem,
} from '../lib/api'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { DesktopShell } from '../components/DesktopShell'

interface StatusProps {
  referenceCode: string
}

/**
 * Status page — the printed program note for this lost item.
 *
 * Design doctrine (2026-05-06 redesign from first principles):
 *
 *   The user is anxious. They lost something. This page is their
 *   hope. It should feel like a respected institution acknowledged
 *   their problem and is doing something thoughtful about it — NOT
 *   like a ticketing dashboard.
 *
 *   Information hierarchy (priority order):
 *     1. The item, dignified — serif headline + quiet object-label
 *        subtitle, like a museum display.
 *     2. Their words, intact — voice transcript stitched into a
 *        flowing italic-serif quote that proves we listened.
 *     3. Where it stands, as a sentence — status as language, not a
 *        colored chip.
 *     4. Captured details — two-column object label list (museum
 *        catalog entry vibe).
 *     5. Their own added notes (if any).
 *     6. Quiet text-link actions — Edit details · Add a note.
 *     7. Reference code — folio-style footer, tiny and persistent.
 *
 *   What we removed (and why):
 *     - Status badge pill (transactional, generic)
 *     - "REFERENCE / LC-XXXX" big eyebrow at top (moved to footer)
 *     - "Your Story" / "Details we caught" eyebrow labels (visual
 *       rhythm carries it)
 *     - PreviewCard reuse (it's a composition affordance; this is a
 *       presentation surface)
 *     - Bulleted-per-turn transcript (broke the flow; now ONE
 *       flowing italic-serif paragraph, like a quoted passage)
 *
 *   Brand alignment: Augusta heritage editorial. DM Serif Display
 *   for the headline, Inter for body. Hairlines for structure. Gold
 *   used surgically (a 24x2 mark above the headline). Accent green
 *   on links only. Generous breathing room between sections.
 */

// snake_case → "Snake Case" plus a few common token tidies.
function pretty(s: string | undefined | null): string {
  if (!s) return ''
  return s
    .toString()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function prettyDate(iso: string | undefined): string {
  if (!iso) return ''
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  if (isNaN(dt.getTime())) return iso
  return dt.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

const JUNK = new Set(['', 'unknown', '<unknown>', 'none', 'n/a', 'na', 'null', 'undefined'])
function prettyValue(key: string, raw: string | undefined): string | undefined {
  if (raw === undefined || raw === null) return undefined
  const t = String(raw).trim()
  if (!t || JUNK.has(t.toLowerCase())) return undefined
  if (key === 'date_occurred' || key === 'created_at') return prettyDate(t)
  return pretty(t)
}

// ── Time-ago — for the "filed N hours ago" byline. Coarse but
// human; we never care about precision below a minute on this page.
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime()
  if (isNaN(then)) return ''
  const ms = Date.now() - then
  const minutes = Math.round(ms / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 30) return `${days} days ago`
  const months = Math.round(days / 30)
  return `${months} month${months === 1 ? '' : 's'} ago`
}

// ── Item identity: noun (headline) + modifiers (subtitle).
//
// Noun cascade: brand+model > model > subcategory > category > "Your item".
// Modifiers: color, material, size, condition. Skipped if empty.
function buildItemNoun(item: Partial<ExtractedItem>): string {
  const brand = prettyValue('brand', item.brand)
  const model = typeof item.model === 'string' && item.model.trim()
    ? pretty(item.model.trim())
    : undefined
  const sub = prettyValue('subcategory', item.subcategory)
  const cat = prettyValue('category', item.category)
  if (brand && model) return `${brand} ${model}`
  if (brand && sub) return `${brand} ${sub}`
  if (model) return model
  if (sub) return sub
  if (cat) return cat
  return 'Your item'
}

function buildItemSubtitle(item: Partial<ExtractedItem>): string {
  const parts: string[] = []
  const color = prettyValue('color_primary', item.color_primary)
  const material = prettyValue('material', item.material)
  const size = typeof item.size === 'string' && item.size.trim() ? item.size.trim() : ''
  const condition = typeof item.condition === 'string' && item.condition.trim()
    ? item.condition.trim()
    : ''
  if (color) parts.push(color.toLowerCase())
  if (material) parts.push(material.toLowerCase())
  if (size) parts.push(size)
  if (condition) parts.push(condition.toLowerCase())
  return parts.join(' · ')
}

// Status sentence — language not chrome. Drives off report.status with
// venue + filing-time injected for specificity.
function buildStatusSentence(report: ItemReport): string {
  const venue = (report.item.venue_name ?? '').toString().trim() || 'the venue'
  const filed = report.created_at ? timeAgo(report.created_at) : ''
  switch (report.status) {
    case 'matched':
      return `${venue} found a possible match. Show your reference code when you arrive.`
    case 'verified':
      return `${venue} confirmed the match. Coordinate pickup with them directly.`
    case 'returned':
      return `Marked returned. Glad it found its way back.`
    case 'expired':
      return `This report has expired. ${venue} may have already cleared their lost-and-found.`
    case 'active':
    case 'open':
    default:
      return filed
        ? `Your report is with ${venue} — filed ${filed}. Anything they find gets checked against it automatically.`
        : `Your report is with ${venue}. Anything they find gets checked against it automatically.`
  }
}

// Where + when + marks lines for the object-label list.
function buildLocationLine(item: Partial<ExtractedItem>): string {
  const venue = prettyValue('venue_name', item.venue_name)
  const city = prettyValue('venue_city', item.venue_city)
  const state = prettyValue('venue_state', item.venue_state)
  const cityState = [city, state].filter(Boolean).join(', ')
  return [venue, cityState].filter(Boolean).join(', ')
}

function buildWhenLine(item: Partial<ExtractedItem>): string {
  const date = prettyDate(item.date_occurred ?? '')
  // Strip a leading "around " from time_approximate so we don't double
  // it when the LLM stored "around 2pm" verbatim. Sentence template
  // owns the prefix; data layer should be the bare time.
  const time = typeof item.time_approximate === 'string'
    ? item.time_approximate.trim().replace(/^around\s+/i, '')
    : ''
  if (date && time) return `${date}, around ${time.toLowerCase()}`
  return date || (time ? `Around ${time.toLowerCase()}` : '')
}

// Stitch turn-per-line transcript into 1–2 flowing paragraphs.
// Light formatting: capitalize, "i" → "I", curly apostrophes,
// trailing period when missing. Splits long transcripts at the
// nearest sentence boundary to the midpoint.
function stitchTranscript(transcript: string): string[] {
  const turns = transcript.split(/\r?\n/).map((t) => t.trim()).filter(Boolean)
  if (turns.length === 0) return []

  const formatted = turns.map((t) => {
    let s = t
    s = s.charAt(0).toUpperCase() + s.slice(1)
    s = s.replace(/\b(i)('?)\b/g, (_m, _i, apos) => 'I' + (apos || ''))
    s = s.replace(/'/g, '’')
    if (!/[.!?]$/.test(s)) s += '.'
    return s
  })

  const joined = formatted.join(' ')
  if (joined.length < 500) return [joined]

  const mid = Math.floor(joined.length / 2)
  const splitIdx = joined.lastIndexOf('. ', mid)
  if (splitIdx > 50) {
    return [
      joined.substring(0, splitIdx + 1).trim(),
      joined.substring(splitIdx + 1).trim(),
    ]
  }
  return [joined]
}

// Editable field definitions for the EditMode grid.
const EDITABLE_FIELDS: { key: keyof ExtractedItem; label: string; placeholder: string }[] = [
  { key: 'category',           label: 'Category',     placeholder: 'what kind' },
  { key: 'subcategory',        label: 'Type',         placeholder: 'type'      },
  { key: 'brand',              label: 'Brand',        placeholder: 'brand'     },
  { key: 'color_primary',      label: 'Color',        placeholder: 'color'     },
  { key: 'material',           label: 'Material',     placeholder: 'material'  },
  { key: 'distinguishing_marks', label: 'Marks',      placeholder: 'marks'     },
  { key: 'venue_name',         label: 'Venue',        placeholder: 'venue'     },
  { key: 'date_occurred',      label: 'Day',          placeholder: 'YYYY-MM-DD' },
  { key: 'time_approximate',   label: 'Time',         placeholder: 'time'      },
]

export default function Status({ referenceCode }: StatusProps) {
  const [report, setReport] = useState<ItemReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const [codeInput, setCodeInput] = useState('')
  const [mode, setMode] = useState<'view' | 'edit' | 'note'>('view')
  // Desktop wrap — Status now uses the same persistent two-pane shell
  // as Home (round-4 fix per E1's #1 critic call: "Status is the
  // single largest hierarchy regression on desktop — apply the
  // desktop shell to /status/:code"). Same brand strip + 40/60 split,
  // with a Status-specific right column instead of the value prop.
  const isDesktop = useMediaQuery('(min-width: 900px)')

  const refetchReport = useCallback(async () => {
    if (!referenceCode) return
    try {
      const data = await getReport(referenceCode)
      setReport(data)
    } catch (err) {
      console.error('refetch failed:', err)
    }
  }, [referenceCode])

  useEffect(() => {
    if (!referenceCode) return
    let cancelled = false
    setLoading(true)
    setError(undefined)

    getReport(referenceCode)
      .then((data) => {
        if (!cancelled) setReport(data)
      })
      .catch((err) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [referenceCode])

  // ── No reference code: input screen (unchanged from previous) ──
  if (!referenceCode) {
    const submit = () => {
      const v = codeInput.trim()
      if (v) window.location.hash = `#/status/${v}`
    }
    const codeInputContent = (
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-4 text-center">
          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'var(--text-h1)',
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: '-0.015em',
              color: 'var(--ink-1)',
            }}
          >
            Check on a report.
          </h1>
          <p className="mt-3 text-[15px]" style={{ color: 'var(--ink-2)' }}>
            Paste your reference code to see what we've found.
          </p>

          <div className="mt-8 w-full max-w-sm flex gap-2">
            <input
              type="text"
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') submit() }}
              placeholder="LC-XXXXXX"
              className="
                flex-1 bg-[var(--surface-1)] border border-[var(--border-default)] rounded-full px-5 py-3
                text-base text-center font-mono tracking-wider
                focus:outline-none focus:border-[var(--border-accent)] focus:ring-2 focus:ring-[var(--accent-glow)]
                transition-colors
              "
              style={{ color: 'var(--ink-1)' }}
              autoFocus
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
            />
            <button
              onClick={submit}
              disabled={!codeInput.trim()}
              className="
                px-5 py-3 rounded-full text-sm font-medium
                bg-[var(--accent)] text-white
                hover:opacity-90 active:opacity-80
                disabled:opacity-40 disabled:cursor-not-allowed
                transition-opacity
              "
            >
              Check
            </button>
          </div>

          <p className="mt-3 text-xs italic" style={{ color: 'var(--ink-4)', fontFamily: 'var(--font-serif)' }}>
            Codes look like <span className="font-mono not-italic">LC-NDFFWC</span>.
          </p>

          <a
            href="#/"
            className="mt-10 text-sm hover:underline"
            style={{ color: 'var(--accent)' }}
          >
            ← Report a lost item
          </a>
        </div>
      </div>
    )
    if (isDesktop) {
      return (
        <DesktopShell right={<StatusRightColumn report={null} loading={false} />}>
          {codeInputContent}
        </DesktopShell>
      )
    }
    return codeInputContent
  }

  const reportContent = (
    <div className="flex-1 min-h-0 flex flex-col px-5 py-8 overflow-y-auto">
      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <span
            className="w-2 h-2 rounded-full"
            style={{
              background: 'var(--ink-3)',
              opacity: 0.6,
              animation: 'thinking-dot 1.4s ease-in-out infinite',
            }}
          />
        </div>
      )}

      {error && (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-2">
          <p className="text-sm mb-4" style={{ color: 'var(--recording)' }}>
            {error.includes('404')
              ? "We couldn't find a report with that code."
              : error.includes('network') || error.includes('fetch')
              ? "Can't reach the server. Check your connection."
              : 'Something went wrong. Please try again.'}
          </p>
          <a href="#/" className="text-sm hover:underline" style={{ color: 'var(--accent)' }}>
            Report a new item
          </a>
        </div>
      )}

      {report && (
        <div className="flex-1 min-h-0 flex flex-col max-w-md mx-auto w-full">
          {mode === 'view' && (
            <ViewMode
              report={report}
              referenceCode={referenceCode}
              onEdit={() => setMode('edit')}
              onAddNote={() => setMode('note')}
            />
          )}

          {mode === 'edit' && (
            <EditMode
              report={report}
              referenceCode={referenceCode}
              onDone={async () => {
                await refetchReport()
                setMode('view')
              }}
              onCancel={() => setMode('view')}
            />
          )}

          {mode === 'note' && (
            <NoteMode
              referenceCode={referenceCode}
              onDone={async () => {
                await refetchReport()
                setMode('view')
              }}
              onCancel={() => setMode('view')}
            />
          )}
        </div>
      )}
    </div>
  )

  if (isDesktop) {
    return (
      <DesktopShell
        right={<StatusRightColumn report={report} error={error} loading={loading} />}
      >
        {reportContent}
      </DesktopShell>
    )
  }
  return reportContent
}

/**
 * StatusRightColumn — the editorial right pane on desktop Status.
 * Listens to the actual report state (per round-3 critic P1: "the
 * status not-found right column lies — 'Your report is on duty'
 * renders even when the code is invalid; an unforgivable inversion
 * of the round-3 'right column listens' promise"). Three voices:
 *
 *   - Loading: quiet "fetching" placeholder, no false promise
 *   - Error / 404: "FRESH START" eyebrow + "Looks like a fresh
 *     start." headline + "Report a new item ↗" promoted to primary
 *   - Report found: "WATCHING / Your report is on duty" + stat
 *     strip with Filed/Venue/Watching + "Report another item ↗"
 */
function StatusRightColumn({
  report,
  error,
  loading,
}: {
  report: ItemReport | null
  error?: string
  loading: boolean
}) {
  // ── State-aware copy: the right column listens. ──
  const variant: 'loading' | 'error' | 'found' | 'idle' =
    loading ? 'loading' :
    error ? 'error' :
    report ? 'found' :
    'idle'

  // Eyebrow + headline + body per state. The CTA (and whether we
  // show the stat strip) flow from this same switch below.
  const copy = (() => {
    if (variant === 'loading') {
      return {
        eyebrow: 'Fetching',
        title: <>One moment.</>,
        body: 'Pulling your report from the venue queue.',
        ctaLabel: null as string | null,
        showStats: false,
      }
    }
    if (variant === 'error') {
      const isNotFound = (error ?? '').includes('404')
      return {
        eyebrow: 'Fresh start',
        title: <>Looks like a {isNotFound ? <em style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}>fresh start</em> : <em style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}>different code</em>}.</>,
        body: isNotFound
          ? "We don't have a report under that code. If you've lost something, file a new one — it takes about two minutes."
          : "We couldn't reach that report. Try again, or file a new one.",
        ctaLabel: 'File a new report',
        showStats: false,
      }
    }
    if (variant === 'found') {
      return {
        eyebrow: 'Watching',
        title: <>Your report is <em style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}>on duty</em>.</>,
        body: "We've handed it to the venue. Anything they find from here on out gets checked against your description automatically — text or email when something fits, no daily digest, no false alarms.",
        ctaLabel: 'Report another item',
        showStats: true,
      }
    }
    // idle (no code yet — code-input screen)
    return {
      eyebrow: 'Check on a report',
      title: <>Paste your <em style={{ fontStyle: 'italic', color: 'var(--ink-2)' }}>reference code</em>.</>,
      body: 'Codes look like LC-NDFFWC. They get sent to you the moment you submit a report.',
      ctaLabel: 'File a new report',
      showStats: false,
    }
  })()

  // Filed-time as a soft relative phrase (5 minutes ago / 2 hours ago / today).
  const filedAgo = (() => {
    if (!report?.created_at) return null
    const then = new Date(report.created_at).getTime()
    if (Number.isNaN(then)) return null
    const ms = Date.now() - then
    const min = Math.floor(ms / 60000)
    if (min < 1) return 'just now'
    if (min < 60) return `${min} min ago`
    const hr = Math.floor(min / 60)
    if (hr < 24) return `${hr} hr ago`
    const day = Math.floor(hr / 24)
    return `${day} day${day === 1 ? '' : 's'} ago`
  })()
  const venueName = (report?.item?.venue_name ?? '').toString().trim() || null

  return (
    <section
      className="flex-1 relative grid overflow-hidden"
      style={{
        background: 'var(--surface-0)',
        color: 'var(--ink-1)',
        alignItems: 'stretch',
        padding:
          'clamp(20px, 2.4vw, 34px) clamp(26px, 3.6vw, 54px) clamp(18px, 2vw, 28px)',
      }}
      aria-label="What happens next"
    >
      <span
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 0,
          background: `
            radial-gradient(ellipse 70% 50% at 110% 8%, rgba(255,255,255,0.6) 0%, transparent 55%),
            radial-gradient(ellipse 90% 70% at -10% 100%, rgba(0, 53, 35, 0.05) 0%, transparent 55%),
            linear-gradient(168deg, rgba(255,255,255,0.6) 0%, var(--surface-0) 38%, rgba(0, 53, 35, 0.03) 100%)
          `,
        }}
      />

      <div
        className="relative flex flex-col h-full min-h-0"
        style={{
          zIndex: 1,
          width: 'min(100%, 720px)',
          margin: '0 auto',
          justifyContent: 'space-between',
          gap: 'clamp(12px, 1.2vw, 22px)',
        }}
      >
        <div
          className="flex flex-col min-h-0"
          style={{ gap: 'clamp(10px, 1vw, 18px)' }}
        >
          <div className="grid" style={{ gap: 'clamp(8px, 0.8vw, 14px)' }}>
            <p
              className="inline-flex items-center"
              style={{
                margin: 0,
                gap: 11,
                color: 'var(--accent)',
                fontFamily:
                  'var(--font-mono, ui-monospace, "SF Mono", Menlo, monospace)',
                fontSize: 'var(--eyebrow-size-sm)',
                fontWeight: 'var(--eyebrow-weight)',
                letterSpacing: 'var(--eyebrow-tracking)',
                lineHeight: 1.2,
                textTransform: 'uppercase',
              }}
            >
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  width: 22,
                  height: 1,
                  background: 'var(--accent)',
                }}
              />
              {copy.eyebrow}
            </p>

            <h1
              style={{
                margin: 0,
                maxWidth: '720px',
                color: 'var(--ink-1)',
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(31px, 3vw, 44px)',
                fontWeight: 400,
                letterSpacing: '-0.018em',
                lineHeight: 1.02,
                textWrap: 'balance',
                fontVariationSettings: '"opsz" 144',
              }}
            >
              {copy.title}
            </h1>

            <p
              style={{
                margin: 0,
                maxWidth: '600px',
                color: 'var(--ink-3)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'clamp(14px, 0.94vw, 15.5px)',
                fontWeight: 400,
                lineHeight: 1.42,
                textWrap: 'pretty',
              }}
            >
              {copy.body}
            </p>
          </div>

          {/* Status detail rows — only when we actually have a report.
              Per round-3 critic P1: "the not-found right column lies —
              'Watching / Ongoing' renders even when the code is invalid." */}
          {copy.showStats && (
            <div
              className="grid"
              style={{
                borderTop: '1px solid var(--border-default)',
                marginTop: 'clamp(2px, 0.35vw, 5px)',
              }}
              aria-label="Report status"
            >
              {[
                { label: 'Filed', value: filedAgo ?? '—' },
                { label: 'Venue', value: venueName ?? '—' },
                { label: 'Watching', value: 'Ongoing' },
              ].map((row, i) => (
                <div
                  key={row.label}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '90px minmax(0, 1fr)',
                    alignItems: 'baseline',
                    gap: 14,
                    padding: 'clamp(7px, 0.8vw, 11px) 0',
                    borderBottom: i < 2 ? '1px solid var(--border-subtle)' : 'none',
                  }}
                >
                  <span
                    style={{
                      margin: 0,
                      color: 'var(--ink-4)',
                      fontFamily:
                        'var(--font-mono, ui-monospace, "SF Mono", Menlo, monospace)',
                      fontSize: 'var(--eyebrow-size-sm)',
                      fontWeight: 'var(--eyebrow-weight)',
                      letterSpacing: 'var(--eyebrow-tracking)',
                      lineHeight: 1.4,
                      textTransform: 'uppercase',
                    }}
                  >
                    {row.label}
                  </span>
                  <span
                    style={{
                      margin: 0,
                      color: 'var(--ink-1)',
                      fontFamily: 'var(--font-serif)',
                      fontSize: 'clamp(17px, 1.32vw, 21px)',
                      fontWeight: 400,
                      letterSpacing: '-0.005em',
                      lineHeight: 1.18,
                    }}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action — copy-driven CTA. Suppressed for loading variant
            (no action to take while fetching). */}
        {copy.ctaLabel && (
          <div
            style={{
              width: '100%',
              maxWidth: '440px',
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr)',
              gap: 'clamp(10px, 0.9vw, 16px)',
              marginTop: 'clamp(6px, 0.7vw, 10px)',
              paddingTop: 'clamp(12px, 1vw, 16px)',
              borderTop: '1px solid var(--border-default)',
            }}
          >
            <a
              href="#/"
              style={{
                position: 'relative',
                width: '100%',
                minHeight: 50,
                padding: '0 22px 0 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 14,
                background: 'var(--ink-1)',
                color: 'var(--surface-0)',
                border: '1px solid var(--ink-1)',
                borderRadius: 0,
                fontFamily: 'var(--font-serif)',
                fontSize: '16px',
                fontWeight: 400,
                fontVariationSettings: '"opsz" 24',
                letterSpacing: '-0.005em',
                lineHeight: 1.15,
                cursor: 'pointer',
                textDecoration: 'none',
                transition:
                  'background 240ms ease, color 240ms ease, border-color 240ms ease, transform 220ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--accent)'
                e.currentTarget.style.borderColor = 'var(--accent)'
                e.currentTarget.style.transform = 'translateY(-1px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--ink-1)'
                e.currentTarget.style.borderColor = 'var(--ink-1)'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
              aria-label={copy.ctaLabel}
            >
              <span style={{ minWidth: 0 }}>{copy.ctaLabel}</span>
              <span
                aria-hidden
                style={{
                  flex: '0 0 auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: 'var(--surface-0)',
                  fontFamily: 'var(--font-serif)',
                  fontSize: '17px',
                  fontWeight: 400,
                  lineHeight: 1,
                  opacity: 0.86,
                }}
              >
                ↗
              </span>
            </a>
          </div>
        )}
      </div>
    </section>
  )
}

// ───────────────────────────────────────────────────────────────────
// ViewMode — the program note. Six vertical sections in priority order.
// ───────────────────────────────────────────────────────────────────

function ViewMode({
  report,
  referenceCode,
  onEdit,
  onAddNote,
}: {
  report: ItemReport
  referenceCode: string
  onEdit: () => void
  onAddNote: () => void
}) {
  const noun = buildItemNoun(report.item)
  const subtitle = buildItemSubtitle(report.item)
  const filedAgo = report.created_at ? timeAgo(report.created_at) : ''
  const verb = report.report_type === 'lost' ? 'Lost' : 'Found'

  // Narrative (when present) is the polished story-form rendering of
  // the voice transcript, generated server-side at submit time. Falls
  // back to the verbatim transcript for legacy reports without one.
  const narrative = (report.item as { narrative?: string }).narrative ?? ''
  const transcript = (report.item as { voice_transcript?: string }).voice_transcript ?? ''
  const paragraphs = narrative
    ? narrative.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
    : stitchTranscript(transcript)

  const statusSentence = buildStatusSentence(report)
  const where = buildLocationLine(report.item)
  const when = buildWhenLine(report.item)
  const marks = prettyValue('distinguishing_marks', report.item.distinguishing_marks)
  const notes = prettyValue('notes', report.item.notes)

  const isClosed = report.status === 'returned' || report.status === 'expired'

  return (
    <article className="flex flex-col gap-8">
      {/* Hero — byline eyebrow, gold mark, item noun, modifier subtitle */}
      <header className="flex flex-col gap-2">
        <p
          className="uppercase eyebrow-token-sm"
          style={{ color: 'var(--ink-3)', fontWeight: 600, margin: 0 }}
        >
          {verb}
          {filedAgo && <span> · Filed {filedAgo}</span>}
        </p>
        <div style={{ width: 24, height: 2, background: 'var(--gold)' }} />
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontWeight: 500,
            fontSize: 'clamp(30px, 7.5vw, 40px)',
            lineHeight: 1.05,
            letterSpacing: '-0.015em',
            color: 'var(--ink-1)',
            margin: 0,
          }}
        >
          {noun}
        </h1>
        {subtitle && (
          <p
            className="text-[15px]"
            style={{
              color: 'var(--ink-2)',
              fontWeight: 400,
              letterSpacing: '0.005em',
              margin: 0,
            }}
          >
            {subtitle}
          </p>
        )}
      </header>

      {/* Their words — flowing italic-serif quote, with a small accent
          opening mark. Falls back to a built sentence for legacy
          reports without a stored transcript. */}
      {paragraphs.length > 0 ? (
        <section className="flex flex-col gap-3">
          <span
            aria-hidden
            style={{
              fontFamily: 'var(--font-serif)',
              color: 'var(--accent)',
              fontSize: '32px',
              lineHeight: 0.5,
              fontStyle: 'italic',
              alignSelf: 'flex-start',
              opacity: 0.6,
            }}
          >
            ❝
          </span>
          {paragraphs.map((p, i) => (
            <p
              key={i}
              style={{
                fontFamily: 'var(--font-serif)',
                fontStyle: 'italic',
                fontSize: 'clamp(17px, 4.5vw, 19px)',
                lineHeight: 1.5,
                color: 'var(--ink-1)',
                margin: 0,
              }}
            >
              {p}
            </p>
          ))}
        </section>
      ) : (
        // Legacy fallback — short prose summary so the page never feels empty.
        <p
          style={{
            fontFamily: 'var(--font-serif)',
            fontStyle: 'italic',
            fontSize: '17px',
            lineHeight: 1.5,
            color: 'var(--ink-2)',
            margin: 0,
          }}
        >
          {[noun, subtitle].filter(Boolean).join(' — ')}.
        </p>
      )}

      {/* Status as language */}
      <section>
        <p
          className="text-[15px]"
          style={{
            color: 'var(--ink-2)',
            lineHeight: 1.55,
            margin: 0,
          }}
        >
          {statusSentence}
        </p>
      </section>

      {/* Captured details — object label list. Hairline above frames
          it as a distinct section without needing a header label. */}
      {(where || when || marks) && (
        <section
          className="pt-6 border-t"
          style={{ borderColor: 'var(--border-subtle)' }}
        >
          <dl className="flex flex-col gap-3">
            {where && <DetailRow label="Where" value={where} />}
            {when && <DetailRow label="When" value={when} />}
            {marks && <DetailRow label="Marks" value={marks} />}
          </dl>
        </section>
      )}

      {/* Notes the user has added (additional_notes / notes column).
          Eyebrow named to feel like THEIR contribution, not a system
          field — encourages adding more. */}
      {notes && (
        <section
          className="pt-6 border-t"
          style={{ borderColor: 'var(--border-subtle)' }}
        >
          <p
            className="uppercase eyebrow-token-sm mb-2"
            style={{ color: 'var(--ink-3)', fontWeight: 600 }}
          >
            Notes you've added
          </p>
          <p
            className="text-[15px]"
            style={{
              color: 'var(--ink-1)',
              lineHeight: 1.55,
              margin: 0,
              whiteSpace: 'pre-wrap',
            }}
          >
            {notes}
          </p>
        </section>
      )}

      {/* Footer — reference code (folio-style) on the left, quiet
          text-link actions on the right. Hairline above sets it
          apart as the page's chrome. */}
      <footer
        className="mt-auto pt-6 border-t flex items-end justify-between gap-4 flex-wrap"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <div className="flex flex-col">
          <span
            className="uppercase eyebrow-token-sm"
            style={{ color: 'var(--ink-3)', fontWeight: 600 }}
          >
            Reference
          </span>
          <span
            className="font-mono"
            style={{
              fontSize: '14px',
              color: 'var(--ink-1)',
              fontWeight: 500,
              letterSpacing: '0.04em',
              marginTop: 2,
            }}
          >
            {referenceCode}
          </span>
        </div>
        {!isClosed && (
          <div
            className="flex items-center gap-3 text-[13px]"
            style={{ color: 'var(--accent)' }}
          >
            <button
              onClick={onEdit}
              className="hover:underline"
              style={{ fontWeight: 500 }}
            >
              Edit details
            </button>
            <span style={{ color: 'var(--ink-4)' }}>·</span>
            <button
              onClick={onAddNote}
              className="hover:underline"
              style={{ fontWeight: 500 }}
            >
              Add a note
            </button>
          </div>
        )}
      </footer>
    </article>
  )
}

// Single row in the object-label list. Label hangs left in tracked
// caps; value flows right in body text. On narrow screens the label
// stays a fixed-ish width so values align in a clean column.
function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-3 sm:gap-4">
      <dt
        className="uppercase eyebrow-token-sm shrink-0"
        style={{
          color: 'var(--ink-3)',
          fontWeight: 600,
          // Narrower label column on small phones (was 4.5rem = 72px,
          // which ate ~20% of a 360px viewport for a 5-letter label).
          // 3rem = 48px on mobile; 4.5rem on ≥640px.
          width: '3rem',
          paddingTop: '2px',
        }}
      >
        {label}
      </dt>
      <dd
        className="text-[15px] flex-1"
        style={{
          color: 'var(--ink-1)',
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        {value}
      </dd>
    </div>
  )
}

// ───────────────────────────────────────────────────────────────────
// EditMode — inline editable grid for the most-relevant fields.
// (Unchanged from previous design — works fine, no need to redesign.)
// ───────────────────────────────────────────────────────────────────

function EditMode({
  report,
  referenceCode,
  onDone,
  onCancel,
}: {
  report: ItemReport
  referenceCode: string
  onDone: () => void | Promise<void>
  onCancel: () => void
}) {
  const [draft, setDraft] = useState<Partial<ExtractedItem>>(() => ({ ...report.item }))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string>()

  const handleChange = (key: keyof ExtractedItem, value: string) => {
    setDraft((prev) => {
      const next = { ...prev }
      if (value === '') delete next[key]
      else next[key] = value
      return next
    })
  }

  const handleSave = async () => {
    if (saving) return
    setSaving(true)
    setErr(undefined)
    try {
      const patch: Record<string, string> = {}
      for (const f of EDITABLE_FIELDS) {
        const before = (report.item[f.key] ?? '') as string
        const after = (draft[f.key] ?? '') as string
        if (before !== after) {
          patch[f.key as string] = after
        }
      }
      if (Object.keys(patch).length === 0) {
        await onDone()
        return
      }
      await updateReport(referenceCode, patch)
      await onDone()
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save — try again.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      <p
        className="uppercase eyebrow-token-sm"
        style={{ color: 'var(--ink-3)', fontWeight: 600 }}
      >
        Edit details
      </p>
      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
        {EDITABLE_FIELDS.map((f) => (
          <EditCell
            key={String(f.key)}
            label={f.label}
            placeholder={f.placeholder}
            value={(draft[f.key] as string | undefined) ?? ''}
            onChange={(v) => handleChange(f.key, v)}
          />
        ))}
      </div>

      {err && (
        <p className="text-[12px]" style={{ color: 'var(--error)' }}>{err}</p>
      )}

      <div
        className="mt-auto flex items-center justify-between gap-3 pt-4 border-t"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <button
          onClick={onCancel}
          className="px-4 py-2 text-[12px] font-medium hover:bg-[var(--surface-2)] transition-colors"
          style={{ color: 'var(--ink-2)', border: '1px solid var(--border-default)', borderRadius: 0 }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2 text-[12px] uppercase font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          style={{
            background: 'var(--accent)',
            color: 'var(--text-on-accent)',
            border: 0,
            borderRadius: 0,
            letterSpacing: '0.14em',
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

function EditCell({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string
  placeholder: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-col">
      <label
        className="text-[11px] tracking-[0.06em] mb-1"
        style={{ color: 'var(--ink-3)', fontWeight: 600 }}
      >
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="text-[14px] px-2 py-1.5 bg-transparent focus:outline-none"
        style={{
          color: 'var(--ink-1)',
          fontWeight: 500,
          borderBottom: '1px solid var(--border-default)',
        }}
      />
    </div>
  )
}

// ───────────────────────────────────────────────────────────────────
// NoteMode — append a freeform detail to the existing notes.
// ───────────────────────────────────────────────────────────────────

function NoteMode({
  referenceCode,
  onDone,
  onCancel,
}: {
  referenceCode: string
  onDone: () => void | Promise<void>
  onCancel: () => void
}) {
  const [text, setText] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string>()
  const taRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    taRef.current?.focus()
  }, [])

  const handleSave = async () => {
    const trimmed = text.trim()
    if (!trimmed || saving) return
    setSaving(true)
    setErr(undefined)
    try {
      await updateReport(referenceCode, { additional_notes: trimmed })
      await onDone()
    } catch (e) {
      setErr(
        e instanceof Error && e.message.includes('409')
          ? "This report is already closed — details can't be added."
          : "Couldn't save — try again in a moment."
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-4">
      <p
        className="uppercase eyebrow-token-sm"
        style={{ color: 'var(--ink-3)', fontWeight: 600 }}
      >
        Add a note
      </p>
      <p className="text-[14px]" style={{ color: 'var(--ink-2)', lineHeight: 1.5 }}>
        Remember anything else? A serial number, what's inside, a sticker — anything that helps tell it apart.
      </p>
      <textarea
        ref={taRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="e.g. There's a hotel keycard in the inner pocket."
        rows={5}
        maxLength={1000}
        className="w-full px-3 py-2 border text-[15px] focus:outline-none focus:border-[var(--border-accent)] resize-none"
        style={{
          borderColor: 'var(--border-default)',
          background: 'var(--surface-1)',
          color: 'var(--ink-1)',
          borderRadius: 0,
          fontFamily: 'inherit',
          lineHeight: 1.5,
        }}
      />

      {err && (
        <p className="text-[12px]" style={{ color: 'var(--error)' }}>{err}</p>
      )}

      <div
        className="mt-auto flex items-center justify-between gap-3 pt-4 border-t"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <button
          onClick={onCancel}
          className="px-4 py-2 text-[12px] font-medium hover:bg-[var(--surface-2)] transition-colors"
          style={{ color: 'var(--ink-2)', border: '1px solid var(--border-default)', borderRadius: 0 }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!text.trim() || saving}
          className="px-5 py-2 text-[12px] uppercase font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
          style={{
            background: 'var(--accent)',
            color: 'var(--text-on-accent)',
            border: 0,
            borderRadius: 0,
            letterSpacing: '0.14em',
          }}
        >
          {saving ? 'Saving…' : 'Save note'}
        </button>
      </div>
    </div>
  )
}
